<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="register-login-landing"
       :class="{ 'register-login-landing--compact': !hasVisibleCobranding }">
    <GdprCookieBanner v-if="isGdprBannerVisible" 
                      @cookie-accept="acceptCookieConsent" />
    <div class="register-login__container">
      <div class="register-login__header">
        <h1 class="register-login__title ds1-utd-h2">
          {{ registrationTitle }}
        </h1>
        <div v-if="coBrandingLogoURL" class="register-login__cobranding">
          <div class="register-login__cobranding-img">
            <img class="register-login__cobranding-img-content"
                 :src="coBrandingLogoURL" 
                 alt="Enterprise logo">
          </div>
        </div>
        <div class="register-login__description">
          <div v-if="isGuestPassUser" class="register-login__description-guest-pass">
            <p class="ds1-mt-0">
              Please enter the information below to activate your free UpToDate trial.
            </p>
            <ul>
              <li v-for="(item, index) in UTD_GUEST_PASS_REGISTRATION_MESSAGES" 
                  :key="index">
                <p>{{ item }}</p>
              </li>
            </ul>
          </div>
          <div v-else-if="!isHasDefaultCobrandingMessage" 
               class="register-login__description-cobranding">
            <div class="register-login__description-cobranding-content">
              <p v-html="coBrandingMessage" />
            </div>
          </div>
        </div>
      </div>
      <RegisterOptionCards v-if="currentState === LANDING" 
                           @on-register-click="startRegister"
                           @on-merge-click="startMerge" />
      <RegistrationForm v-else-if="currentState === REGISTER"
                        @cancel-registration="cancelRegistration" />
      <div v-if="canContinueWithSearch && currentState === LANDING"
           class="register-login__footer">
        <UtdButton id="skipButton"
                   button-size="large"
                   button-style="ghost"
                   @click="continueToSearch">
          Continue with your search
        </UtdButton>
      </div>
    </div>
    <MergeAccountsModal ref="mergeAccountsModal"
                        class="merge-accounts-modal"
                        @cancel-merge="cancelMerge" />
    <BcdrNoticeModal ref="registerBcdrModal" feature-name="UpToDate Registration" />
    <BcdrNoticeModal ref="mergeBcdrModal" feature-name="Merge" />
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref, computed, watch, nextTick } from 'vue';
import { useStore } from 'vuex';
import { SET_SHOULD_HIDE_HEADER_ITEMS } from '_acaSrc/store/feature.store';
import RegisterOptionCards from './RegisterOptionCards.vue';
import RegistrationForm from './RegistrationForm.vue';
import MergeAccountsModal from './MergeAccountsModal.vue';
import {
    C_REG_MERGE,
    UTD_REGISTER_BENEFITS_ARRAY,
    DEFAULT_COBRANDING_MESSAGES,
    C_HTTP,
    UTD_GUEST_PASS_REGISTRATION_MESSAGES,
    GUEST_PASS_LICENSE,
    C_ACCOUNT
} from '_acaSrc/utility/constants';
import UtdButton from '_acaSrc/components/shared/stdlib/UtdButton.vue';
import useRegistrationForm from '_acaSrc/composables/registrationForm.composable';
import GdprCookieBanner from '_acaSrc/components/header/GdprCookieBanner.vue';
import { getCookie } from '_acaSrc/utility/Cookies';
import BcdrNoticeModal from '../error/BcdrNoticeModal.vue';
import useMergeAccounts from '_acaSrc/composables/mergeAccounts.composable';
import { getQueryParamValues } from '_acaSrc/utility/http';
import { getWindow } from '_acaSrc/utility/DOM';

const LOADING = 0;
const LANDING = 1;
const REGISTER = 2;

const currentState = ref(LOADING); 
const store = useStore();

const isDisasterRecovery = computed(() => store.getters['app/isDisasterRecovery']);
const registerBcdrModal = ref(null);
const mergeBcdrModal = ref(null);
const { mergeError, mergeData, checkConsolidateErrorStatus } = useMergeAccounts();

const startRegister = () => {
    if (isDisasterRecovery.value) {
        registerBcdrModal.value.openModal();
        return;
    }
    currentState.value = REGISTER;
};

const mergeAccountsModal = ref(null);
const startMerge = () => {
    if (isDisasterRecovery.value) {
        mergeBcdrModal.value.openModal();
        return;
    }
    mergeAccountsModal.value.openModal();
};

const cancelMerge = () => {
    mergeAccountsModal.value.closeModal();
};

const cancelRegistration = () => {
    currentState.value = LANDING;
};

const shouldCloseGdprBanner = ref(false);
const acceptCookieConsent = () => {
    shouldCloseGdprBanner.value = true;
};
const isAcceptCookieConsent = computed(() => getCookie('cookie-accept') === 't');
const isGdprBannerVisible = computed(() => 
    store.getters['feature/isShowGdprBanner'] 
    && !isAcceptCookieConsent.value
    && !shouldCloseGdprBanner.value
);

const continueWithSearchLink = computed(() => registrationInfo.value.continueWithSearchLink);
const continueToSearch = () => {
    if (!continueWithSearchLink.value) {
        router.value.url(C_HTTP.DEFAULT_HOMEPAGE_PATH);
        return;
    }
    router.value.url(continueWithSearchLink.value);
};

const registrationInfo = computed(() => store.getters['login/registrationInfo']);
const canContinueWithSearch = computed(() => registrationInfo.value.canContinueWithSearch);
const coBrandingLogoURL = computed(() => registrationInfo.value.coBrandingLogoURL);
const coBrandingMessage = computed(() => registrationInfo.value.coBrandingMessage);
const coBrandingHeader = computed(() => registrationInfo.value.coBrandingHeader);
const isHasDefaultCobrandingMessage = computed(() => (
    DEFAULT_COBRANDING_MESSAGES.includes(coBrandingMessage.value)));
const hasVisibleCobranding = computed(() =>
    !!coBrandingLogoURL.value
    || isGuestPassUser.value
    || (!!coBrandingMessage.value && !isHasDefaultCobrandingMessage.value)
);

const router = computed(() => store.getters['app/router']);
const isGuestPassUser = computed(() => store.getters['footer/licensee'] === GUEST_PASS_LICENSE
  || store.getters['profile/userProfileVisitor'].isGuestUser);
const registrationTitle = computed(() => {
    if (coBrandingHeader.value) {
        return coBrandingHeader.value;
    }
    if (isGuestPassUser.value) {
        return 'Register for Important Updates';
    }
    if (isOidcUser.value) {
        return 'Complete your registration';
    }
    return 'Register for a new account';
});

const handleFindAccountError = async(errorStatus) => {
    const isConsolidateErrorType = checkConsolidateErrorStatus(errorStatus);
    if (isConsolidateErrorType) {
        return;
    }
    mergeError.title = C_ACCOUNT.FORM_MESSAGING.MERGE_ERROR_TITLE;
    mergeError.message = errorStatus
        || C_ACCOUNT.FORM_MESSAGING.MERGE_FAILURE_TYPE.MERGE_RULE_VIOLATION;
    mergeData.error = true;
    await nextTick();
    startMerge();
};

const oidcMergeStatus = computed(() => store.getters['login/oidcMergeStatus']);
watch(oidcMergeStatus, (newVal) => {
    if (newVal && newVal !== '' && newVal !== C_REG_MERGE.OIDC_MERGE_SUCCESS) {
        handleFindAccountError(newVal);
    }
}, { immediate: true });

const isOidcUser = computed(() => store.getters['auth/isOidcUser']);
const isAuthenticatedByIp = computed(() => store.getters['app/isAuthenticatedByIp']);
const { prefillFormFields } = useRegistrationForm();

// Automatically load the registration form for guest pass, BYOL, OIDC, or anonymous users
const registrationForm = computed(() => store.getters['login/registrationForm']);
const registrationFormReady = computed(() => registrationForm.value.isLoaded);
watch(registrationFormReady, (newValue) => {
    if (newValue) {
        prefillFormFields();
        if (isGuestPassUser.value
          || registrationForm.value.formName === C_REG_MERGE.GUEST_PASS_USER_FORM
          || isOidcUser.value
          || isAuthenticatedByIp.value
        ) {
            currentState.value = REGISTER;
            return;
        }
        currentState.value = LANDING;
        
        const openMerge = getQueryParamValues(getWindow().location.href, 'openMerge');
        if (openMerge === 'true') {
            nextTick(() => {
                startMerge();
            });
        }
    }
}, { immediate: true });

onMounted(() => {
    store.commit(`feature/${SET_SHOULD_HIDE_HEADER_ITEMS}`, true);
});
onUnmounted(() => {
    store.commit(`feature/${SET_SHOULD_HIDE_HEADER_ITEMS}`, false);   
});

</script>

<style lang="less" scoped>
@import (reference) '~_acaAssets/wkce/colors/wkce-app-styles';

@cobranding-logo-width-desktop: 310px;
@cobranding-logo-width-mobile: 288px;
@cobranding-logo-height: 75px;

@media only screen and (min-width: 768px) {
   .register-login__cobranding {
    .register-login__cobranding-img {
      width: @cobranding-logo-width-desktop;
    }
  
    .register-login__cobranding-img-content {
      max-width: @cobranding-logo-width-desktop;
    }
  }
  
  .register-login__description-cobranding-content {
    width: 75%;
  }
}

.register-login-landing {
  .ds1-ph-3();
  .ds1-pv-5();
  .ds1-flex-horizontal-center();
  flex-direction: column;
  align-items: center;
  font-size: 14px;
}

.register-login__header {
  .ds1-ma-0();
  width: 100%;
}

.register-login__container {
  .ds1-flex-horizontal-center();
  max-width: 690px;
  flex-direction: column;
  align-items: center;
}

.register-login__title {
  .ds1-mt-0();
  text-align: left;
}

.register-login__cobranding {
  .ds1-flex-vertical-center();
  background-color: transparent;
  flex-direction: column;
  width: 100%;
  height: @cobranding-logo-height;
}

.register-login__cobranding-img {
  .ds1-flex-vertical-center();
  background-color: transparent;
  flex-direction: column;
  width: @cobranding-logo-width-mobile;
  height: @cobranding-logo-height;
}

.register-login__cobranding-img-content {
  object-fit: cover;
  align-items: center;
  max-width: @cobranding-logo-width-mobile;
  max-height: @cobranding-logo-height;
}

.register-login__description {
  .ds1-flex-horizontal-center();
  flex-direction: column;
  align-items: center;
}

.register-login__description-cobranding {
  .ds1-flex-horizontal-center();
}

.register-login__description-cobranding-content {
  line-height: 1.75;
}

.register-login-landing--compact {
  .register-login__header {
    align-self: flex-start;
  }

  .register-login__title {
    .ds1-mb-0();
    font-size: 22px;
  }

  :deep(.registration-form__privacy-policy) {
    .ds1-mt-1();
    .ds1-mb-3();
  }
}

.register-login__footer {
  width: 100%;
  display: flex;
  justify-content: flex-end;

  #skipButton {
    .ds1-mt-7();
    .ds1-mr-3();
    .ds1-utd-weight-500();
    font-size: 14px;
    padding-right: 20px;
    padding-left: 20px;
    white-space: nowrap;
  }
}

</style>